import Cookies from 'js-cookie';
import { AuthCookies, domain } from './auth-constants';

// Removes cookies by name under every domain scope they may have been written with. Cookies may
// have been written under different domain scopes (e.g. .tinuiti.com vs .blisspoint.tinuiti.com),
// so all variants are removed to avoid stale cookies causing auth loops on logout/sign-in.
export const removeCookiesAllScopes = (cookieNames: readonly string[]): void => {
  const { hostname } = window.location;
  const parts = hostname.split('.');
  const domainsToRemove = new Set([domain, hostname, `.${hostname}`]);

  // Also target the parent domain (e.g. "tinuiti.com" / ".tinuiti.com")
  if (parts.length >= 3) {
    const parentDomain = parts.slice(-2).join('.');
    domainsToRemove.add(parentDomain);
    domainsToRemove.add(`.${parentDomain}`);
  }

  for (const d of domainsToRemove) {
    for (const name of cookieNames) {
      Cookies.remove(name, { domain: d });
    }
  }
  // Also remove without a domain attribute to catch any cookies scoped to the exact origin
  for (const name of cookieNames) {
    Cookies.remove(name);
  }
};

// Amplify (the pre-PLATFORM-988 client) kept its session in localStorage under these prefixes.
const LEGACY_LOCAL_STORAGE_PREFIXES = ['CognitoIdentityServiceProvider.', 'amplify-'];

// Readable cookies the old client wrote, limited to the names this app owns (AuthCookies): generic
// names such as id_token or refresh_token may belong to other apps on the parent domain.
// google_api_access_token (and its expiration) are deliberately NOT here: they belong to the Drive
// consent gate, which still uses them.
const LEGACY_COOKIE_NAMES: readonly string[] = [
  ...Object.values(AuthCookies).filter(
    name =>
      name !== AuthCookies.GOOGLE_API_ACCESS_TOKEN &&
      name !== AuthCookies.GOOGLE_API_ACCESS_TOKEN_EXPIRATION
  )
];

/**
 * One-time boot purge of tokens the old Amplify client left in this browser (PLATFORM-988: the
 * tokens-only-in-memory goal has to hold for existing users too, not just new sessions).
 * Idempotent and cheap: a localStorage key scan plus one cookie-jar read, and it only writes when
 * something legacy is actually present. Never throws: storage can be blocked.
 */
export const purgeLegacyAuthState = (): void => {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && LEGACY_LOCAL_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable (blocked, private mode) — nothing to purge there.
  }
  try {
    const present = Cookies.get();
    const stale = LEGACY_COOKIE_NAMES.filter(name => name in present);
    if (stale.length > 0) {
      removeCookiesAllScopes(stale);
    }
  } catch {
    // document.cookie unavailable — nothing to purge.
  }
};
